// Shared video-generation payload builder.
//
// Both the single-shot Pre-Production modal and the bulk "Generate All
// Scene/Project Videos" flow MUST produce identical request payloads for the
// same shot. This module centralises the logic so the bulk path becomes
// "loop over shots and call individual flow N times" — exactly what the
// frontend product spec calls for.

import { getAllVideoModels } from './aiService';
//
// Inputs:
//   shot             - Shot object (with .preProduction, .dialogues, .textOverlays, etc.)
//   projectData      - { characters, aspectRatio, ... }
//   characters       - characters relevant to this shot (caller may pass [] to use projectData)
//
// Output:
//   {
//     finalPrompt,     // string — same shape as VideoPreProductionModal.constructFinalPrompt
//     dialoguesData,   // snake_case array — same shape as modal's structured payload
//     cinematicActions,// snake_case array — same shape as modal's payload
//     duration,        // seconds (matches modal's computeContentDuration)
//     hasEndFrame,
//     startFrameImage, endFrameImage,
//     aspectRatio
//   }

const parseSeconds = (v) => {
  if (v === undefined || v === null) return null;
  if (typeof v === 'number') return v;
  const m = String(v).match(/[\d.]+/);
  return m ? parseFloat(m[0]) : null;
};

/**
 * Resolve the user-selected video model to a canonical backend model id.
 * Reads `localStorage.selectedAIModels[<capability>]` and matches it against
 * the live `getAllVideoModels()` list (exact then partial match).
 *
 * Returns: { id, name, capabilityKey, hasEndFrame } or null on hard failure.
 *
 * IMPORTANT: This is intentionally the SAME logic that Storyboard and
 * VideoPreProductionModal use. The result is stamped onto
 * `shot.preProduction.selectedVideoModel` immediately after pre-production
 * returns, so subsequent bulk/modal paths can skip the network round-trip
 * and fallback-hunting behaviour when the resolver races `preloadAvailableModels`.
 */
export const resolveSelectedVideoModel = async (hasEndFrame, isReferencesToVideo = false) => {
  let selectedMap = {};
  try { selectedMap = JSON.parse(localStorage.getItem('selectedAIModels') || '{}'); }
  catch { selectedMap = {}; }

  // Panel-based shots use the `references-to-video` capability. The model
  // pool, default-key lookup, and fallback ladder are all distinct from the
  // legacy image-to-video / start-end-frame pools — see REFERENCES_TO_VIDEO_SPEC.md.
  //
  // IMPORTANT: We do NOT cross-capability fall back here. A start-end-frame
  // or image-to-video model key cannot consume `reference_images[]` on the
  // backend; if we accidentally returned such a key the request would be
  // routed to the wrong renderer (e.g. backend pipes the panels through a
  // legacy 2-frame model and ignores all but the first/last). The selection
  // is restricted to the refs-to-video aliases only — if none are saved,
  // the live catalog pool is the source of truth.
  const capabilityKey = isReferencesToVideo
    ? 'references to video'
    : (hasEndFrame ? 'start-end-frame videos' : 'Image to video');
  let rawKey;
  if (isReferencesToVideo) {
    // Feb 13 2026 — Case-insensitive lookup. Admin catalogs sometimes
    // save the capability key as `'References to video'` (capital R with
    // spaces). Iterate every localStorage key and match by lowercased
    // alias so the user's AI Settings pick is honoured regardless of the
    // exact capital-letter form the admin uses.
    const REFS_ALIASES = new Set([
      'references to video',
      'references-to-video',
      'references_to_video',
      'referencestovideo',
      'reference to video',
      'reference-to-video',
      'reference_to_video',
      'referencetovideo',
    ]);
    for (const [k, v] of Object.entries(selectedMap || {})) {
      if (REFS_ALIASES.has(String(k).toLowerCase().trim())) {
        rawKey = v;
        break;
      }
    }
    // NOTE: Do NOT fall back to `start-end-frame videos` or `Image to video`
    // here — those keys belong to incompatible capabilities and would be
    // matched against the (concatenated) pool below, returning a model
    // that cannot consume reference_images[]. See bug report Feb 2026.
  } else if (hasEndFrame) {
    rawKey = selectedMap['start-end-frame videos']
      || selectedMap['start-end-frame video']
      || selectedMap['startEndFrame']
      || selectedMap['start_end_frame_videos']
      || selectedMap['Image to video'];
  } else {
    rawKey = selectedMap['Image to video']
      || selectedMap['image-to-video']
      || selectedMap['imageToVideo']
      || selectedMap['image_to_video'];
  }

  try {
    const all = await getAllVideoModels();
    let pool;
    if (isReferencesToVideo) {
      // STRICT: refs-to-video shots can ONLY be served by refs-to-video
      // models. Cross-capability fallback was removed (Feb 2026) — sending
      // a start-end-frame or image-to-video model_key alongside
      // reference_images[] causes the backend to silently route to the
      // wrong renderer and ignore the panel ordering.
      pool = [...(all.referencesToVideo || [])];
    } else if (hasEndFrame) {
      pool = [...(all.startEndFrame || []), ...(all.imageToVideo || [])];
    } else {
      pool = [...(all.imageToVideo || []), ...(all.startEndFrame || [])];
    }
    const transformed = pool.map(m => ({
      id: m.key || m.id || m.name,
      name: m.name || m.key || m.id
    }));
    if (rawKey) {
      const needle = String(rawKey).toLowerCase().trim();
      const match = transformed.find(m => {
        const id = (m.id || '').toLowerCase().trim();
        const name = (m.name || '').toLowerCase().trim();
        return id === needle || name === needle ||
          id.includes(needle) || needle.includes(id) ||
          name.includes(needle) || needle.includes(name);
      });
      if (match) return { id: match.id, name: match.name, capabilityKey, hasEndFrame, isReferencesToVideo, stampVersion: 2 };
      // No catalog match BUT the user explicitly saved this model key
      // (e.g. via Tier Selection or the AI Toolkit). Honour their pick
      // verbatim — the backend will look up the key in its own registry,
      // which often contains tier-specific models that the public model
      // catalog doesn't expose (the catalog's `models[]` array is a subset
      // of `tiers.*[]`). See bug Feb 28, 2026: Economy tier wrote
      // `pixverse-ref2video` to localStorage, but the catalog only listed
      // `seedance2-ref2video` in `models[]` → resolver silently returned
      // the catalog's first entry (= Premium), ignoring the tier pick.
      return { id: rawKey, name: rawKey, capabilityKey, hasEndFrame, isReferencesToVideo, stampVersion: 2 };
    }
    // No saved selection AND no match — last resort is the catalog default.
    // This is the only path where we accept whatever the catalog lists first.
    if (transformed.length > 0) {
      return { id: transformed[0].id, name: transformed[0].name, capabilityKey, hasEndFrame, isReferencesToVideo, stampVersion: 2 };
    }
  } catch { /* swallow — fall through */ }

  if (rawKey) return { id: rawKey, name: rawKey, capabilityKey, hasEndFrame, isReferencesToVideo, stampVersion: 2 };
  return null;
};

/**
 * Stamp both `selectedVideoModelImageToVideo` and `selectedVideoModelStartEndFrame`
 * onto each shot's `preProduction`. We stamp BOTH variants because pre-production
 * typically runs BEFORE frame generation — at that moment we don't yet know
 * whether the shot will have one or two frames. Storing both lets the resolver
 * pick the correct one at video-generation time based on the shot's current
 * frame state, instead of relying on a stale `hasEndFrame` snapshot from
 * pre-production time.
 *
 * For backwards compatibility we also keep `selectedVideoModel` populated with
 * the start-end-frame variant when it exists, otherwise the image-to-video one.
 *
 * Pure: returns a new shots array; does not mutate input.
 */
export const stampSelectedVideoModelOnShots = async (shots) => {
  if (!Array.isArray(shots) || shots.length === 0) return shots;
  // Resolve all three variants once for the whole batch (network calls coalesce
  // through the cached `getAllVideoModels()`).
  const [imgToVideo, startEnd, refsToVideo] = await Promise.all([
    resolveSelectedVideoModel(false).catch(() => null),
    resolveSelectedVideoModel(true).catch(() => null),
    resolveSelectedVideoModel(false, true).catch(() => null),
  ]);
  return shots.map((sh) => ({
    ...sh,
    preProduction: {
      ...(sh.preProduction || {}),
      selectedVideoModelImageToVideo: imgToVideo,
      selectedVideoModelStartEndFrame: startEnd,
      selectedVideoModelReferencesToVideo: refsToVideo,
      // Keep legacy field too — prefer refs-to-video if shot uses panels,
      // else start-end-frame, else image-to-video.
      selectedVideoModel: (Array.isArray(sh?.panels) && sh.panels.some(p => !!p?.image))
        ? (refsToVideo || startEnd || imgToVideo || null)
        : (startEnd || imgToVideo || null)
    }
  }));
};


const formatSec = (n) => `${Number(n || 0).toFixed(1)}s`;

// Estimate speech duration — handles non-Latin scripts (Kannada, Hindi, etc.)
// IDENTICAL to VideoPreProductionModal.estimateSpeechDuration.
export const estimateSpeechDuration = (text) => {
  if (!text || !text.trim()) return 0;
  const wordCount = text.trim().split(/\s+/).length;
  const charCount = text.trim().length;
  const wordEstimate = Math.ceil(wordCount / 2.5);
  const charEstimate = Math.ceil(charCount * 0.08);
  return Math.max(2, Math.max(wordEstimate, charEstimate) + 1);
};

/**
 * Build the dialogues map keyed by characterId — same shape modal stores in
 * its `dialogues` state. Honours admin-supplied timing from
 * shot.preProduction.dialogues; falls back to sequential estimation when
 * admin timing is unavailable for a character.
 */
export const buildDialoguesMap = (shot, projectCharacters, shotCharacters) => {
  const allChars = [
    ...(shotCharacters || []),
    ...(projectCharacters || []).filter(c => c.category === 'narrator')
  ];

  // 1. Index admin timing + admin text by characterId
  const adminTimingByChar = {};
  const adminTextByChar = {};
  const preProdDialogues = Array.isArray(shot.preProduction?.dialogues) ? shot.preProduction.dialogues : [];
  for (const ad of preProdDialogues) {
    if (!ad?.characterId) continue;
    const start = parseSeconds(ad.startTime ?? ad.start_time);
    const end = parseSeconds(ad.endTime ?? ad.end_time);
    if (
      !adminTimingByChar[ad.characterId] ||
      (start !== null && start < (adminTimingByChar[ad.characterId].startTime ?? Infinity))
    ) {
      adminTimingByChar[ad.characterId] = { startTime: start, endTime: end };
    }
    if (!adminTextByChar[ad.characterId] && ad.text) {
      adminTextByChar[ad.characterId] = ad.text;
    }
  }

  // 2. Group user-authored shot.dialogues per character (concatenate multi-line)
  const sortedUser = [...(shot.dialogues || [])].sort((a, b) => {
    if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
    if (a.index !== undefined && b.index !== undefined) return a.index - b.index;
    const aTime = parseFloat(a.startTime || a.start_time || '0');
    const bTime = parseFloat(b.startTime || b.start_time || '0');
    return aTime - bTime;
  });
  const charMap = {};
  sortedUser.forEach((d, idx) => {
    const cid = d.characterId;
    if (!cid) return;
    if (!charMap[cid]) {
      charMap[cid] = {
        texts: [],
        order: d.order ?? idx,
        characterName: d.characterName,
        // Feb 2026 — carry the pre-generated GCS audio URL from
        // shot.dialogues[].audioUrl (populated by the pre-prod modal
        // when a character voice was assigned).
        audioUrl: d.audioUrl || d.audio_url || null,
        voiceId:  d.voiceId  || d.voice_id  || null,
      };
    }
    if (d.text) charMap[cid].texts.push(d.text);
    // First non-empty audioUrl wins for a given character (matches text concat).
    if (!charMap[cid].audioUrl && (d.audioUrl || d.audio_url)) {
      charMap[cid].audioUrl = d.audioUrl || d.audio_url;
    }
    if (!charMap[cid].voiceId && (d.voiceId || d.voice_id)) {
      charMap[cid].voiceId = d.voiceId || d.voice_id;
    }
  });

  // 3. Build dialogue entries per character
  const initial = {};
  let cursor = 0;
  Object.entries(charMap).forEach(([cid, data]) => {
    const ch = allChars.find(c => c.id === cid);
    if (!ch) return;
    const userText = data.texts.join(' ').trim();
    const text = userText || adminTextByChar[cid] || '';
    if (!text) return;
    const dur = estimateSpeechDuration(text);
    const adminTiming = adminTimingByChar[cid];
    let start, end;
    if (adminTiming && adminTiming.startTime !== null && adminTiming.startTime !== undefined) {
      start = adminTiming.startTime;
      end = (adminTiming.endTime !== null && adminTiming.endTime !== undefined)
        ? adminTiming.endTime
        : start + dur;
    } else {
      start = cursor;
      end = cursor + dur;
      cursor = end;
    }
    initial[cid] = {
      startTimecode: formatSec(start),
      endTimecode: formatSec(end),
      text,
      order: data.order,
      audioUrl: data.audioUrl || null,
      voiceId:  data.voiceId  || null,
    };
  });

  // 4. Surface admin-only dialogues (admin has timing+text, user didn't author)
  Object.entries(adminTimingByChar).forEach(([cid, t]) => {
    if (initial[cid]) return;
    const ch = allChars.find(c => c.id === cid);
    if (!ch) return;
    const text = (adminTextByChar[cid] || '').trim();
    if (!text) return;
    const dur = estimateSpeechDuration(text);
    const start = t.startTime ?? 0;
    const end = t.endTime ?? (start + dur);
    initial[cid] = {
      startTimecode: formatSec(start),
      endTimecode: formatSec(end),
      text,
      order: 500
    };
  });

  // 5. Narrator (uses shot.narration as text source)
  const narrator = (projectCharacters || []).find(c => c.category === 'narrator');
  if (narrator && !initial[narrator.id]) {
    const text = shot.narration || '';
    if (text) {
      const dur = estimateSpeechDuration(text) || 3;
      initial[narrator.id] = {
        startTimecode: formatSec(cursor),
        endTimecode: formatSec(cursor + dur),
        text,
        order: 999
      };
    }
  }

  return initial;
};

/**
 * Build the timed actions array — same shape modal stores in `timedActions`
 * state. Flattens preProduction.character_actions[].timeline[].
 */
export const buildTimedActions = (shot, totalDurationGuess) => {
  const preProd = shot.preProduction || {};
  const rawActions = preProd.character_actions || preProd.actions || preProd.video_prompt?.actions || [];
  const flattened = [];

  if (Array.isArray(rawActions) && rawActions.length > 0) {
    if (rawActions[0]?.timeline || rawActions[0]?.character_name) {
      rawActions.forEach((charAction) => {
        const charName = charAction.character_name || charAction.characterName || 'Character';
        (charAction.timeline || []).forEach((entry) => {
          let startSec = 0;
          const tc = entry.timecode || entry.time || '0';
          if (String(tc).includes(':')) {
            const [mm, ss] = String(tc).split(':');
            startSec = (parseInt(mm) || 0) * 60 + (parseFloat(ss) || 0);
          } else {
            startSec = parseFloat(tc) || 0;
          }
          const durMs = entry.duration_ms || 2000;
          const endSec = startSec + durMs / 1000;
          const text = entry.action || entry.description || '';
          flattened.push({
            startTime: formatSec(startSec),
            endTime: formatSec(endSec),
            action: `${charName}: ${text}${entry.emotion ? ` [${entry.emotion}]` : ''}`,
            character: charName,
            emotion: entry.emotion || null
          });
        });
      });
    } else {
      const total = Math.max(1, totalDurationGuess || rawActions.length);
      rawActions.forEach((a, idx) => {
        const start = a.start_time ?? a.startTime ?? a.start;
        const end = a.end_time ?? a.endTime ?? a.end;
        flattened.push({
          startTime: start !== undefined && start !== null ? formatSec(parseSeconds(start) ?? 0) : formatSec(idx * (total / rawActions.length)),
          endTime: end !== undefined && end !== null ? formatSec(parseSeconds(end) ?? 0) : formatSec((idx + 1) * (total / rawActions.length)),
          action: a.action || a.description || a.text || '',
          character: a.character || null,
          emotion: a.emotion || null
        });
      });
    }
  }

  // Fallback to main_prompt as a single block when no actions present
  if (flattened.length === 0) {
    const main = preProd.cinematicPrompt || preProd.cinematic_prompt
      || preProd.video_prompt?.main_prompt || preProd.video_prompt?.prompt
      || preProd.main_prompt || preProd.prompt
      || shot.description || shot.startFramePrompt || '';
    if (main) {
      flattened.push({
        startTime: '0.0s',
        endTime: formatSec(totalDurationGuess || 8),
        action: main,
        character: null,
        emotion: null
      });
    }
  }

  return flattened;
};

/**
 * Compute content-driven duration the same way the modal does:
 *   max(8, ceil(maxDialogueEnd), narrationEstimate)
 */
export const computeContentDuration = (dialoguesMap, shot) => {
  const maxDialogueEnd = Object.values(dialoguesMap || {}).reduce((max, d) => {
    const end = parseFloat(d.endTimecode) || 0;
    return Math.max(max, end);
  }, 0);
  const narrationDuration = estimateSpeechDuration(shot.narration || '');
  return Math.max(8, Math.ceil(maxDialogueEnd), narrationDuration);
};

/**
 * Construct the final prompt — IDENTICAL to VideoPreProductionModal.constructFinalPrompt.
 *
 * ASMR mode: when `audioStyle === 'asmr'`, every speech/music/effect section
 * is stripped and replaced with a single [ASMR] tag. This forces the video
 * model to render ONLY ASMR audio — no dialogue, narration, SFX, VFX, BGM,
 * or ambient. The ambient field (if populated) is reused as the ASMR cue
 * because it's the closest semantic match; otherwise a sensible default is
 * emitted so the model always gets explicit ASMR direction.
 */
export const constructFinalPrompt = ({
  cameraShot,
  timedActions,
  dialoguesMap,
  sfx, vfx, bgm, ambient,
  textOverlays,
  projectCharacters,
  narrator,
  audioStyle = 'both'
}) => {
  const cinematicActions = (timedActions || [])
    .filter(a => a.action?.trim())
    .map(a => `@[${a.startTime}-${a.endTime}] ${a.action.trim()}`)
    .join(' ');

  let finalPrompt = `[${cameraShot || ''}] ${cinematicActions}`.trim();

  if (audioStyle === 'asmr') {
    // Strip all other audio; replace with a single ASMR directive.
    const asmrCue = (ambient && ambient.trim())
      || 'Close-mic ASMR: crisp, intimate textures that match the on-screen action. No voices, no music, no traditional SFX.';
    finalPrompt += ` [ASMR]: ${asmrCue}.`;
  } else {
    // Dialogue (non-narrator)
    const dialogueEntries = Object.entries(dialoguesMap || {})
      .filter(([charId]) => (projectCharacters || []).find(c => c.id === charId)?.category !== 'narrator')
      .filter(([, D]) => typeof D.text === 'string' && D.text.trim() !== '')
      .map(([charId, D]) => {
        const charName = (projectCharacters || []).find(c => c.id === charId)?.name || 'Character';
        return `@[${D.startTimecode}-${D.endTimecode}] ${charName}: "${D.text.trim()}"`;
      });
    if (dialogueEntries.length > 0) {
      finalPrompt += ` [Dialogue]: ${dialogueEntries.join(' ')}.`;
    }

    // Narration
    if (narrator && dialoguesMap?.[narrator.id]?.text?.trim()) {
      finalPrompt += ` [Narration]: "${dialoguesMap[narrator.id].text.trim()}".`;
    }

    if (sfx?.trim()) finalPrompt += ` [SFX]: ${sfx.trim()}.`;
    if (vfx?.trim()) finalPrompt += ` [VFX]: ${vfx.trim()}.`;
    if (bgm?.trim()) finalPrompt += ` [BGM]: ${bgm.trim()}.`;
    if (ambient?.trim()) finalPrompt += ` [Ambient]: ${ambient.trim()}.`;
  }

  const textOverlayEntries = (textOverlays || []).map(o => {
    if (!o.content?.trim()) return '';
    if (o.aiStyle) {
      return `Add text overlay: "${o.content}". Use a cinematic style that fits the scene.`;
    }
    return `Add text overlay: "${o.content}". Font: ${o.fontFamily}, Size: ${o.fontSize}px, Color: ${o.color}.`;
  }).filter(Boolean);
  if (textOverlayEntries.length > 0) {
    finalPrompt += ` [Text Overlays]: ${textOverlayEntries.join(' ')}`;
  }

  return finalPrompt.trim();
};

/**
 * Build the snake_case structured dialogues payload — IDENTICAL to modal.
 *
 * Feb 2026 — Narrator lines are ALSO included so their pre-generated TTS
 * `audio_url` reaches the video model as an audio reference (same shape as
 * character dialogue audio references). The `[Narration]: "…"` line in the
 * final prompt is kept for the model's textual context; this payload adds
 * the audible track alongside it.
 */
export const buildDialoguesPayload = (dialoguesMap, projectCharacters, shotCharacters) => {
  return Object.entries(dialoguesMap || {})
    .filter(([charId, D]) => {
      const ch = (projectCharacters || []).find(c => c.id === charId)
        || (shotCharacters || []).find(c => c.id === charId);
      const hasText = typeof D.text === 'string' && D.text.trim() !== '';
      return ch && hasText;
    })
    .map(([charId, D]) => {
      const ch = (projectCharacters || []).find(c => c.id === charId)
        || (shotCharacters || []).find(c => c.id === charId);
      return {
        character_id: charId,
        character_name: ch?.name || 'Character',
        start_time: D.startTimecode,
        end_time: D.endTimecode,
        text: D.text.trim(),
        order: D.order || 0,
        character_image: ch?.image || null,
        // Feb 2026 — voice payload for the video model.
        voice_id: D.voiceId || ch?.elevenLabsVoiceId || ch?.voiceId || null,
        audio_url: D.audioUrl || null,
      };
    })
    .sort((a, b) => (parseFloat(a.start_time) || 0) - (parseFloat(b.start_time) || 0));
};

/**
 * Build the snake_case cinematic_actions payload — IDENTICAL to modal.
 */
export const buildCinematicActionsPayload = (timedActions) => {
  return (timedActions || [])
    .filter(a => a.action?.trim())
    .map(a => ({
      start_time: a.startTime,
      end_time: a.endTime,
      action: a.action.trim(),
      character: a.character || null,
      emotion: a.emotion || null
    }));
};

/**
 * One-call helper: returns everything `aiService.generateVideo()` needs for a
 * shot, using the SAME logic the modal would apply when auto-populating from
 * shot.preProduction. Used by the bulk-generation flow so it produces an
 * identical payload to clicking "Generate Video" individually.
 */
export const buildShotVideoPayload = (shot, projectData, shotCharacters) => {
  const projectCharacters = projectData?.characters || [];
  const audioStyle = projectData?.audioStyle || 'both';
  // In ASMR mode, no dialogue/narration audio is generated — pass an empty
  // map to the prompt builder and the dialogues payload so no speech is
  // produced by the video model.
  const dialoguesMap = audioStyle === 'asmr'
    ? {}
    : buildDialoguesMap(shot, projectCharacters, shotCharacters);
  const duration = computeContentDuration(dialoguesMap, shot);
  const timedActions = buildTimedActions(shot, duration);

  const preProd = shot.preProduction || {};
  const cameraShot = preProd.camera?.movement || preProd.camera?.shot || preProd.cameraShot || '';

  // Audio cues — use modal-equivalent text fields.
  // Modal seeds these from preProd at modal-mount time; bulk replicates that.
  const audio = preProd.audio || {};
  let sfx = '';
  if (Array.isArray(audio.sfx) && audio.sfx.length > 0) {
    sfx = audio.sfx
      .map(s => {
        const snd = s.sound || s.description || s;
        const t = s.timecode ? ` @${s.timecode}` : '';
        return `${snd}${t}`;
      })
      .filter(Boolean)
      .join('; ');
  }
  let bgm = '';
  if (audio.bgm) {
    const txt = audio.bgm.description || audio.bgm.text || '';
    const mood = audio.bgm.mood ? ` (${audio.bgm.mood})` : '';
    const tempo = audio.bgm.tempo_bpm ? ` - ${audio.bgm.tempo_bpm} BPM` : '';
    bgm = (txt + mood + tempo).trim();
  }
  const ambient = audio.ambient?.description || '';
  let vfx = '';
  const vfxData = preProd.vfx || preProd.visual_effects;
  if (Array.isArray(vfxData) && vfxData.length > 0) {
    vfx = vfxData.map(v => v.effect || v.description || v).filter(Boolean).join('; ');
  } else if (typeof vfxData === 'string') {
    vfx = vfxData.trim();
  }

  const textOverlays = Array.isArray(shot.textOverlays) ? shot.textOverlays : [];

  const narrator = projectCharacters.find(c => c.category === 'narrator');

  const finalPrompt = constructFinalPrompt({
    cameraShot,
    timedActions,
    dialoguesMap,
    sfx, vfx, bgm, ambient,
    textOverlays,
    projectCharacters,
    narrator,
    audioStyle
  });

  const dialoguesData = buildDialoguesPayload(dialoguesMap, projectCharacters, shotCharacters);
  const cinematicActions = buildCinematicActionsPayload(timedActions);

  // Panel-based shots (Feb 2026): the video is generated via references-to-video.
  // The ordered panel images REPLACE the legacy start_frame_image / end_frame_image
  // pair in the request body. We forward them as `referenceImages` and explicitly
  // null out the start/end fields so the backend doesn't see two competing
  // representations of the same shot.
  //
  // Legacy shots (no panels) keep the existing start/end frame behaviour.
  const panelImagesInOrder = Array.isArray(shot?.panels)
    ? shot.panels.map(p => p?.image).filter(Boolean)
    : [];
  const isPanelBased = panelImagesInOrder.length > 0;

  return {
    finalPrompt,
    dialoguesData,
    cinematicActions,
    duration,
    hasEndFrame: isPanelBased ? (panelImagesInOrder.length > 1) : !!shot.endFrameImage,
    isReferencesToVideo: isPanelBased,
    startFrameImage: isPanelBased ? null : shot.startFrameImage,
    endFrameImage:   isPanelBased ? null : (shot.endFrameImage || null),
    referenceImages: isPanelBased ? panelImagesInOrder : null,
    aspectRatio: projectData?.aspectRatio || '16:9'
  };
};
